import { Injectable, inject } from '@angular/core';
import { format, parse } from 'date-fns';
import { Observable } from 'rxjs';
import { GetProductPricesQuery, PricingService, ProductPricesViewModel } from 'src/app/api';
import { CombinedStore } from 'src/app/core/combined.store';
import { YearHouseBuilded } from 'src/app/shared/enum/house-builded.enum';
import { SurfaceDependance } from 'src/app/shared/enum/housing.enum';
import { SpaceOfRoom } from 'src/app/shared/enum/spaceOfRoom.enum';
import { getRiskItems } from 'src/app/shared/utils/riskitem-util';
import { getDefaultDeductibleTypeByPath } from '../enum/deductible-type.enum';

@Injectable({
  providedIn: 'root',
})
export class PriceService {
  constructor(private pricingService: PricingService) {}

  private store = inject(CombinedStore);

  /**
   * Construit et soumet une demande de tarification.
   * @param couponCode Code promo saisi (null si absent)
   * @param sponsorEmail Email du parrain (optionnel)
   */
  getProductPrices(couponCode: string | null, sponsorEmail?: string | null): Observable<ProductPricesViewModel> {
    let currentDate: Date = new Date();
    const effectDate = this.store.declaration().effect_date;
    if (typeof effectDate === 'string') {
      currentDate = parse(effectDate, 'dd/MM/yyyy', new Date());
    }

    const riskItems = getRiskItems(this.store);

    const productPrices: GetProductPricesQuery = {
      contextId: this.store.context().token,
      productCode: this.store.path(),
      distributorCode: this.store.context().codeVendeur,
      promotionCode: couponCode !== '' ? couponCode : null,
      requestedStartEffectiveDate: format(currentDate, 'yyyy-MM-dd'),
      riskItems: riskItems,
      email: this.store.subscriber().email,
      sponsorEmail: sponsorEmail ?? undefined,
      lessorName: this.store.declaration().lessor?.name ?? '',
      address: {
        zipCode:
          this.store.declaration().address?.addressAutocomplete?.zipCode ?? this.store.declaration().address?.zipCode,
        city: this.store.declaration().address?.addressAutocomplete?.city ?? this.store.declaration().address?.city,
        country: this.store.declaration().address?.country?.countryCode,
        streetName:
          this.store.declaration().address?.addressAutocomplete?.street ??
          this.store.declaration().address?.address?.split(' ').slice(1).join(' '),
        streetNumber:
          this.store.declaration().address?.addressAutocomplete?.streetNumber ??
          this.store.declaration().address?.address?.split(' ').slice(0, 1).join(' '),
      },
      subscriptionInformation: {
        floor: this.store.declaration().apartmentFloor!,
        housingType: this.store.declaration().housingType!,
        residenceType: this.store.declaration().residenceType,
        occupationStatus: this.store.declaration().qualiteOccupant!,
        hasGreenEnergyProductionInstallation: !!this.store.declaration().durableDevelopmentInstallation,
        hasSwimmingPool: !!this.store.declaration().hasSwimmingPoolSpaOrJacuzzi,
        roomCount: this.store.declaration().numberOfRooms?.numberOfRooms,
        dependencyType: this.store.declaration().dependances?.hasOtherDependance
          ? (this.store.declaration().dependances?.otherDependanceSurface ?? null)
          : SurfaceDependance.Aucune,
        roomCountGt40M2: this.store.declaration().numberOfRooms?.between41And75 ?? 0,
        roomCountGt76M2: this.store.declaration().numberOfRooms?.moreThan76 ?? 0,
        verandaType: this.store.declaration().numberOfRooms?.hasVeranda
          ? (this.store.declaration().numberOfRooms?.hasVeranda ?? '')
          : SpaceOfRoom.Aucune,
        constructionYear: this.store.declaration().dateHouseBuilted ?? YearHouseBuilded.Inconnue,
        roommatesNumber: this.store.declaration().nbColoc ?? 0,
        deductibleType: this.store.declaration().deductibleType ?? getDefaultDeductibleTypeByPath(this.store.path()),
      },
    };

    return this.pricingService.apiPricesPost(productPrices);
  }
}
